import type { CanvasItem, DrawingClientEvent, DrawingServerEvent, Point, StrokeItem } from "../../../shared/types";
import { applyCanvasEvent, createCanvasReducerState, type CanvasReducerState } from "../../../shared/canvasReducer";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "../../../shared/palette";

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const num = parseInt(clean, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

const FILL_TOLERANCE = 40;

export interface ForgetfulnessConfig {
  delayMs: number;
  fadeMs: number;
}

export interface LagConfig {
  delayMs: number;
}

export interface CanvasEffects {
  /** 0 = off, 1 = 110x75, 2 = 75x51, 3 = 44x30 - see cccCatalog.ts's Pixelated I/II/III. */
  pixelLevel: 0 | 1 | 2 | 3;
  /** 0 = off, 1 = grayscale, 2 = black & white. */
  monochromeLevel: 0 | 1 | 2;
  forgetfulness: ForgetfulnessConfig | null;
  lag: LagConfig | null;
}

const NO_EFFECTS: CanvasEffects = { pixelLevel: 0, monochromeLevel: 0, forgetfulness: null, lag: null };

interface DrawTarget {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  pixelMode: boolean;
}

/**
 * Deterministic replay renderer. Mirrors the exact same ordered command log the
 * server keeps (via the shared canvasReducer), so every client - artist included -
 * ends up with pixel-identical output without ever transmitting bitmap data.
 *
 * Every drawing primitive (dot/segment/floodFill) targets whatever getDrawTarget()
 * currently returns - the full-resolution "source" canvas normally, or a genuinely
 * tiny offscreen canvas (110x75 / 75x51) when Pixelated is active. Drawing directly
 * at the small resolution - rather than rendering at full detail and averaging it
 * down afterwards - is what makes thin strokes reliably show up (a floored minimum
 * line width in the tiny canvas's own coordinate space) instead of sometimes
 * vanishing into a barely-tinted average, and keeps edges crisp (pixel-snapped
 * coordinates) instead of flickering between black and gray depending on exactly
 * where a line fell relative to the downsample grid.
 */
export class CanvasRenderer {
  private state: CanvasReducerState = createCanvasReducerState();
  private ctx: CanvasRenderingContext2D;
  private sourceCanvas: HTMLCanvasElement;
  private sourceCtx: CanvasRenderingContext2D;
  private pixelCanvas: HTMLCanvasElement | null = null;
  private pixelCtx: CanvasRenderingContext2D | null = null;
  private pixelDims: { w: number; h: number } | null = null;
  private effects: CanvasEffects = NO_EFFECTS;
  /** Each fill's flood-filled pixel set, computed once and reused on every
   * later redraw - see floodFill() for why this exists. */
  private fillMaskCache = new Map<string, { w: number; h: number; indices: Uint32Array }>();
  /** When each stroke's strokeEnd was applied - see computeStrokeAlpha for why
   * "Lag"/"Forgetfulness" need this instead of just stroke.timestamp (which is
   * set once at strokeStart and never updated). */
  private strokeCompletedAt = new Map<string, number>();
  /** Drives periodic full redraws for any time-based visibility effect
   * (Forgetfulness's fade-out, Lag's delayed-appear) - both need to keep
   * re-evaluating stroke alpha against the clock even with no new draw events
   * arriving, since a stroke's timestamp alone doesn't otherwise trigger a
   * repaint. */
  private temporalEffectsTimer: ReturnType<typeof setInterval> | null = null;
  /** Pending requestAnimationFrame for present() - see schedulePresent(). */
  private presentRafId: number | null = null;
  /** Snapshot of the committed drawing (current draw target, before any tool
   * preview) - lets the Line tool's live preview restore it with one drawImage
   * per pointermove instead of replaying every item of the turn. Only trusted
   * while previewBaseValid; see repaintCommitted(). */
  private previewBase: HTMLCanvasElement | null = null;
  private previewBaseValid = false;

  constructor(private canvas: HTMLCanvasElement) {
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;

    this.sourceCanvas = document.createElement("canvas");
    this.sourceCanvas.width = CANVAS_WIDTH;
    this.sourceCanvas.height = CANVAS_HEIGHT;
    const sourceCtx = this.sourceCanvas.getContext("2d", { willReadFrequently: true });
    if (!sourceCtx) throw new Error("2D canvas context unavailable");
    this.sourceCtx = sourceCtx;

    this.clearToWhite();
    this.present();
  }

  /** Applied uniformly for every effect except Upside Down, which - per the spec -
   * only changes what OTHER players see, not the artist, and is a pure CSS
   * transform on the canvas element itself (wired in canvasView.ts). */
  setEffects(effects: CanvasEffects): void {
    const pixelLevelChanged = effects.pixelLevel !== this.effects.pixelLevel;
    this.effects = effects;
    this.previewBaseValid = false;
    if (this.temporalEffectsTimer) {
      clearInterval(this.temporalEffectsTimer);
      this.temporalEffectsTimer = null;
    }
    if (effects.forgetfulness || effects.lag) {
      this.temporalEffectsTimer = setInterval(() => this.redrawAll(), 150);
    }
    if (pixelLevelChanged) {
      // Rebuild from the true item log at the new target resolution rather than
      // just re-presenting whatever's already on whichever canvas was active.
      this.redrawAll();
    } else {
      this.present();
    }
  }

  destroy(): void {
    if (this.temporalEffectsTimer) {
      clearInterval(this.temporalEffectsTimer);
      this.temporalEffectsTimer = null;
    }
    if (this.presentRafId !== null) {
      cancelAnimationFrame(this.presentRafId);
      this.presentRafId = null;
    }
  }

  syncFromServer(items: CanvasItem[]): void {
    this.state = createCanvasReducerState();
    this.state.items = items.slice();
    this.fillMaskCache.clear();
    this.strokeCompletedAt.clear();
    this.previewBaseValid = false;
    this.redrawAll();
  }

  applyServerEvent(event: DrawingServerEvent): void {
    this.apply(event, event.timestamp ?? Date.now());
  }

  applyLocalEvent(event: DrawingClientEvent): void {
    this.apply(event, Date.now());
  }

  /** Repaints only the committed item log - used to clear a live tool preview overlay. */
  repaintCommitted(): void {
    // Lag/Forgetfulness make every stroke's alpha a function of the clock, so a
    // snapshot is never reusable while either is active - replay for real.
    if (this.previewBaseValid && this.previewBase && !this.effects.forgetfulness && !this.effects.lag) {
      const t = this.getDrawTarget();
      if (this.previewBase.width === t.width && this.previewBase.height === t.height) {
        t.ctx.drawImage(this.previewBase, 0, 0);
        this.schedulePresent();
        return;
      }
    }
    this.redrawAll();
    this.capturePreviewBase();
  }

  /** Draws a temporary (uncommitted) preview segment, e.g. while dragging the line tool. */
  previewSegment(a: Point, b: Point, color: string, size: number): void {
    this.repaintCommitted();
    this.drawSegment(a, b, color, size);
    this.schedulePresent();
  }

  private capturePreviewBase(): void {
    if (this.effects.forgetfulness || this.effects.lag) return;
    const t = this.getDrawTarget();
    if (!this.previewBase) this.previewBase = document.createElement("canvas");
    if (this.previewBase.width !== t.width || this.previewBase.height !== t.height) {
      this.previewBase.width = t.width;
      this.previewBase.height = t.height;
    }
    const ctx = this.previewBase.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, t.width, t.height);
    ctx.drawImage(t.ctx.canvas, 0, 0);
    this.previewBaseValid = true;
  }

  private apply(event: DrawingClientEvent, timestamp: number): void {
    this.previewBaseValid = false;
    applyCanvasEvent(this.state, event, timestamp);
    switch (event.type) {
      case "strokeStart": {
        const stroke = this.state.inProgress.get(event.id);
        // Skip the live incremental draw while Lag is still hiding this stroke -
        // otherwise it would flash into view immediately here and only vanish
        // again on the next periodic redraw, instead of staying hidden the
        // whole delay like the card describes.
        if (stroke && this.computeStrokeAlpha(stroke) > 0) this.drawDot(stroke.points[0]!, stroke.color, stroke.size);
        break;
      }
      case "strokePoint": {
        const stroke = this.state.inProgress.get(event.id);
        if (stroke && stroke.points.length >= 2 && this.computeStrokeAlpha(stroke) > 0) {
          const prev = stroke.points[stroke.points.length - 2]!;
          const curr = stroke.points[stroke.points.length - 1]!;
          this.drawSegment(prev, curr, stroke.color, stroke.size);
        }
        break;
      }
      case "strokePoints": {
        // Same segment-by-segment drawing as that many individual strokePoints,
        // so a batched stroke looks pixel-identical to an unbatched one.
        const stroke = this.state.inProgress.get(event.id);
        const added = Math.floor(event.points.length / 2);
        if (stroke && added > 0 && this.computeStrokeAlpha(stroke) > 0) {
          const first = Math.max(1, stroke.points.length - added);
          for (let i = first; i < stroke.points.length; i++) {
            this.drawSegment(stroke.points[i - 1]!, stroke.points[i]!, stroke.color, stroke.size);
          }
        }
        break;
      }
      case "strokeEnd":
        this.strokeCompletedAt.set(event.id, timestamp);
        break;
      case "fill": {
        const last = this.state.items[this.state.items.length - 1];
        if (last && last.kind === "fill") this.floodFill(last.id, last.point, last.color);
        break;
      }
      case "clear":
        this.clearToWhite();
        break;
      case "undo":
      case "redo":
        this.redrawAll();
        break;
    }
    this.schedulePresent();
  }

  private redrawAll(): void {
    this.clearToWhite();
    for (const item of this.state.items) {
      if (item.kind === "stroke") this.drawFullStroke(item);
      else if (item.kind === "fill") this.floodFill(item.id, item.point, item.color);
      else if (item.kind === "clear") this.clearToWhite();
    }
    this.schedulePresent();
  }

  /** Copying the draw target to the visible canvas (plus Monochrome's full-canvas
   * pixel remap) used to run once per drawing event - often several times per
   * frame while someone draws quickly. Deferring it to the next animation frame
   * coalesces all of those into one copy per frame; the browser couldn't have
   * shown the intermediate states anyway. */
  private schedulePresent(): void {
    if (this.presentRafId !== null) return;
    this.presentRafId = requestAnimationFrame(() => {
      this.presentRafId = null;
      this.present();
    });
  }

  /** Resolves where drawing primitives should currently target: the full-res
   * source canvas normally, or the tiny pixel-art canvas (created/resized lazily)
   * when Pixelated is active. */
  private getDrawTarget(): DrawTarget {
    const { pixelLevel } = this.effects;
    if (pixelLevel === 0) {
      return { ctx: this.sourceCtx, width: CANVAS_WIDTH, height: CANVAS_HEIGHT, scaleX: 1, scaleY: 1, pixelMode: false };
    }
    const [pw, ph] = pixelLevel === 3 ? [44, 30] : pixelLevel === 2 ? [75, 51] : [110, 75];
    if (!this.pixelCanvas || this.pixelDims?.w !== pw || this.pixelDims?.h !== ph) {
      this.pixelCanvas = document.createElement("canvas");
      this.pixelCanvas.width = pw;
      this.pixelCanvas.height = ph;
      const ctx = this.pixelCanvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) throw new Error("2D canvas context unavailable");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, pw, ph);
      this.pixelCtx = ctx;
      this.pixelDims = { w: pw, h: ph };
    }
    return { ctx: this.pixelCtx!, width: pw, height: ph, scaleX: pw / CANVAS_WIDTH, scaleY: ph / CANVAS_HEIGHT, pixelMode: true };
  }

  private clearToWhite(): void {
    const t = this.getDrawTarget();
    t.ctx.fillStyle = "#ffffff";
    t.ctx.fillRect(0, 0, t.width, t.height);
  }

  /** "Lag": a stroke is fully invisible until lag.delayMs after it was drawn,
   * then pops in at full opacity - applied first since a still-lagged stroke
   * should stay hidden regardless of what Forgetfulness would otherwise say.
   * "Forgetfulness": a stroke younger than delayMs is fully opaque, then fades
   * linearly to invisible over the following fadeMs. Fills/clears are unaffected
   * by either card - only pen/line strokes count, per their own descriptions. */
  private computeStrokeAlpha(stroke: StrokeItem): number {
    if (this.state.inProgress.has(stroke.id)) {
      // Still being drawn - a long, slowly-drawn stroke can otherwise be held
      // open past lag.delayMs/forgetfulness.delayMs, both of which are
      // measured from when the stroke was DONE, not started. Without this,
      // such a stroke would pop into view (Lag) or start fading (Forgetfulness)
      // while the artist is still actively drawing it.
      return this.effects.lag ? 0 : 1;
    }
    const age = Date.now() - (this.strokeCompletedAt.get(stroke.id) ?? stroke.timestamp);
    if (this.effects.lag && age < this.effects.lag.delayMs) return 0;
    const config = this.effects.forgetfulness;
    if (!config) return 1;
    if (age <= config.delayMs) return 1;
    const fadeElapsed = age - config.delayMs;
    if (fadeElapsed >= config.fadeMs) return 0;
    return 1 - fadeElapsed / config.fadeMs;
  }

  /** Scales a full-res point into the current draw target's coordinate space. */
  private scalePoint(p: Point, t: DrawTarget): Point {
    return { x: p.x * t.scaleX, y: p.y * t.scaleY };
  }

  /** Rounds a scaled point down to an integer low-res pixel coordinate, for the
   * manual rasterizer below - fillRect on integer coordinates is the only way to
   * guarantee a pixel-mode stroke is either fully-on or fully-off with no partial
   * coverage. Canvas 2D's own stroke()/arc() path rendering always anti-aliases
   * (imageSmoothingEnabled only affects drawImage, per present() below), so even
   * a "+0.5 snapped" vector line still blends at the edges of any non-axis-aligned
   * segment - which is exactly the "line sometimes turns black and sometimes
   * gray" artifact this rasterizer exists to eliminate. */
  private toIntPixel(p: Point, t: DrawTarget): { x: number; y: number } {
    return { x: Math.floor(p.x * t.scaleX), y: Math.floor(p.y * t.scaleY) };
  }

  /** In pixel mode, floors the effective line width at 1px of the tiny canvas so a
   * thin brush can never dilute away to nothing - it always paints at least one
   * full low-res pixel, which is what actually shows up once upscaled. */
  private scaleLineWidth(size: number, t: DrawTarget): number {
    if (!t.pixelMode) return size;
    return Math.max(1, Math.round(size * t.scaleX));
  }

  /** Stamps a solid square of `size` low-res pixels centered on (x, y). Used
   * instead of ctx.arc()/stroke() in pixel mode so every painted pixel is fully
   * opaque - fillRect on integer coordinates has no partial-coverage edge case. */
  private stampPixel(t: DrawTarget, x: number, y: number, size: number): void {
    const half = Math.floor(size / 2);
    t.ctx.fillRect(x - half, y - half, size, size);
  }

  /** Bresenham line rasterization between two integer low-res points, stamping a
   * `size`-pixel square at every step so thick strokes stay solid with no
   * anti-aliased edge pixels - the pixel-art equivalent of ctx.lineTo()+stroke(). */
  private rasterizeLine(t: DrawTarget, x0: number, y0: number, x1: number, y1: number, size: number): void {
    const dx = Math.abs(x1 - x0);
    const sx = x0 < x1 ? 1 : -1;
    const dy = -Math.abs(y1 - y0);
    const sy = y0 < y1 ? 1 : -1;
    let err = dx + dy;
    let x = x0;
    let y = y0;
    for (;;) {
      this.stampPixel(t, x, y, size);
      if (x === x1 && y === y1) break;
      const e2 = 2 * err;
      if (e2 >= dy) {
        err += dy;
        x += sx;
      }
      if (e2 <= dx) {
        err += dx;
        y += sy;
      }
    }
  }

  private drawFullStroke(stroke: StrokeItem): void {
    if (stroke.points.length === 0) return;
    const alpha = this.computeStrokeAlpha(stroke);
    if (alpha <= 0) return;
    if (stroke.tool === "line") {
      const first = stroke.points[0]!;
      const last = stroke.points[stroke.points.length - 1]!;
      this.drawSegment(first, last, stroke.color, stroke.size, alpha);
      return;
    }
    if (stroke.points.length === 1) {
      this.drawDot(stroke.points[0]!, stroke.color, stroke.size, alpha);
      return;
    }

    const t = this.getDrawTarget();
    const lineWidth = this.scaleLineWidth(stroke.size, t);
    t.ctx.save();
    t.ctx.globalAlpha = alpha;

    if (t.pixelMode) {
      t.ctx.fillStyle = stroke.color;
      let prev = this.toIntPixel(stroke.points[0]!, t);
      this.stampPixel(t, prev.x, prev.y, lineWidth);
      for (let i = 1; i < stroke.points.length; i++) {
        const curr = this.toIntPixel(stroke.points[i]!, t);
        this.rasterizeLine(t, prev.x, prev.y, curr.x, curr.y, lineWidth);
        prev = curr;
      }
      t.ctx.restore();
      return;
    }

    // Straight point-to-point segments, deliberately NOT smoothed via
    // quadraticCurveTo - this must render pixel-identically to how the stroke
    // was drawn live (apply()'s "strokePoint" case below, which draws each
    // segment with a plain drawSegment() call), since ANY redraw (undo/redo,
    // an effects/pixel-level change, syncFromServer for a late joiner) replays
    // every prior stroke through this exact path. A smoothed replay here would
    // visibly change already-committed strokes the moment any of those trigger
    // a redraw - most obvious with Tremble, where the smoothing quietly erases
    // its characteristic jaggy jitter.
    t.ctx.strokeStyle = stroke.color;
    t.ctx.lineWidth = lineWidth;
    t.ctx.lineCap = "round";
    t.ctx.lineJoin = "round";
    t.ctx.beginPath();
    const p0 = this.scalePoint(stroke.points[0]!, t);
    t.ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < stroke.points.length; i++) {
      const p = this.scalePoint(stroke.points[i]!, t);
      t.ctx.lineTo(p.x, p.y);
    }
    t.ctx.stroke();
    t.ctx.restore();
  }

  private drawDot(point: Point, color: string, size: number, alpha = 1): void {
    const t = this.getDrawTarget();
    const width = this.scaleLineWidth(size, t);
    t.ctx.save();
    t.ctx.globalAlpha = alpha;
    t.ctx.fillStyle = color;
    if (t.pixelMode) {
      const p = this.toIntPixel(point, t);
      this.stampPixel(t, p.x, p.y, width);
    } else {
      const p = this.scalePoint(point, t);
      t.ctx.beginPath();
      t.ctx.arc(p.x, p.y, width / 2, 0, Math.PI * 2);
      t.ctx.fill();
    }
    t.ctx.restore();
  }

  private drawSegment(a: Point, b: Point, color: string, size: number, alpha = 1): void {
    const t = this.getDrawTarget();
    const width = this.scaleLineWidth(size, t);
    t.ctx.save();
    t.ctx.globalAlpha = alpha;
    if (t.pixelMode) {
      t.ctx.fillStyle = color;
      const pa = this.toIntPixel(a, t);
      const pb = this.toIntPixel(b, t);
      this.rasterizeLine(t, pa.x, pa.y, pb.x, pb.y, width);
    } else {
      const pa = this.scalePoint(a, t);
      const pb = this.scalePoint(b, t);
      t.ctx.strokeStyle = color;
      t.ctx.lineWidth = width;
      t.ctx.lineCap = "round";
      t.ctx.lineJoin = "round";
      t.ctx.beginPath();
      t.ctx.moveTo(pa.x, pa.y);
      t.ctx.lineTo(pb.x, pb.y);
      t.ctx.stroke();
    }
    t.ctx.restore();
  }

  /** Flood-fills once, then remembers exactly which pixels that filled so every
   * later redraw (undo/redo, an effects/pixel-level change, the 150ms temporal-
   * effects tick) just re-paints that same frozen set instead of re-running the
   * boundary search. Re-running it live would use whatever the canvas looks
   * like AT THAT MOMENT as the flood's walls - and "Forgetfulness" strokes that
   * bounded this fill can by then have faded past their alpha cutoff and
   * stopped being drawn at all (see drawFullStroke's `alpha <= 0` skip), so the
   * fill would spill through wherever its walls used to be. Freezing the pixel
   * set the first time (while those walls are still fresh) keeps a fill looking
   * exactly like it did when it was placed, and turns every later redraw of it
   * into a cheap direct write instead of a full getImageData+BFS+putImageData
   * over the whole canvas - the fix for the reported "spills over and gets
   * laggy" behavior, which compounds with every past fill on every 150ms tick. */
  private floodFill(id: string, start: Point, fillColorHex: string): void {
    const t = this.getDrawTarget();
    const cached = this.fillMaskCache.get(id);
    if (cached && cached.w === t.width && cached.h === t.height) {
      this.applyFillMask(t, cached.indices, fillColorHex);
      return;
    }

    const x0 = Math.round(start.x * t.scaleX);
    const y0 = Math.round(start.y * t.scaleY);
    if (x0 < 0 || y0 < 0 || x0 >= t.width || y0 >= t.height) return;

    const imageData = t.ctx.getImageData(0, 0, t.width, t.height);
    const data = imageData.data;
    const width = t.width;
    const height = t.height;

    const startOffset = (y0 * width + x0) * 4;
    const tr = data[startOffset]!;
    const tg = data[startOffset + 1]!;
    const tb = data[startOffset + 2]!;
    const ta = data[startOffset + 3]!;
    const [fr, fg, fb] = hexToRgb(fillColorHex);

    if (tr === fr && tg === fg && tb === fb && ta === 255) {
      this.fillMaskCache.set(id, { w: width, h: height, indices: new Uint32Array(0) });
      return;
    }

    // Scanline fill over typed arrays (the same algorithm the server's gallery
    // renderer uses). It recolors exactly the same 4-connected region of pixels
    // within FILL_TOLERANCE of the start color as the old pixel-by-pixel version
    // did - only visited pixels are ever recolored, so every pixel still being
    // tested holds its original color - just without allocating an array per
    // pixel, which made a large fill stall the page for a noticeable moment.
    const visited = new Uint8Array(width * height);
    const filled = new Uint32Array(width * height);
    let filledCount = 0;
    const matches = (pixel: number): boolean => {
      if (visited[pixel]) return false;
      const o = pixel * 4;
      return (
        Math.abs(data[o]! - tr) <= FILL_TOLERANCE &&
        Math.abs(data[o + 1]! - tg) <= FILL_TOLERANCE &&
        Math.abs(data[o + 2]! - tb) <= FILL_TOLERANCE &&
        Math.abs(data[o + 3]! - ta) <= FILL_TOLERANCE
      );
    };

    const seeds: number[] = [x0, y0];
    while (seeds.length > 0) {
      const y = seeds.pop()!;
      const x = seeds.pop()!;
      const row = y * width;
      if (!matches(row + x)) continue;

      let left = x;
      while (left > 0 && matches(row + left - 1)) left--;
      let right = x;
      while (right < width - 1 && matches(row + right + 1)) right++;

      for (let i = left; i <= right; i++) {
        const pixel = row + i;
        visited[pixel] = 1;
        filled[filledCount++] = pixel;
        const o = pixel * 4;
        data[o] = fr;
        data[o + 1] = fg;
        data[o + 2] = fb;
        data[o + 3] = 255;
      }

      for (let ny = y - 1; ny <= y + 1; ny += 2) {
        if (ny < 0 || ny >= height) continue;
        const nrow = ny * width;
        let inRun = false;
        for (let i = left; i <= right; i++) {
          if (matches(nrow + i)) {
            if (!inRun) seeds.push(i, ny);
            inRun = true;
          } else {
            inRun = false;
          }
        }
      }
    }

    t.ctx.putImageData(imageData, 0, 0);
    this.fillMaskCache.set(id, { w: width, h: height, indices: filled.slice(0, filledCount) });
  }

  /** Re-paints a previously computed fill mask (see floodFill) - a plain write
   * over the exact same pixel indices, no boundary search. */
  private applyFillMask(t: DrawTarget, indices: Uint32Array, fillColorHex: string): void {
    if (indices.length === 0) return;
    const [fr, fg, fb] = hexToRgb(fillColorHex);
    const imageData = t.ctx.getImageData(0, 0, t.width, t.height);
    const data = imageData.data;
    for (let i = 0; i < indices.length; i++) {
      const dataIdx = indices[i]! * 4;
      data[dataIdx] = fr;
      data[dataIdx + 1] = fg;
      data[dataIdx + 2] = fb;
      data[dataIdx + 3] = 255;
    }
    t.ctx.putImageData(imageData, 0, 0);
  }

  /** Copies the current draw target onto the visible canvas, upscaling with
   * nearest-neighbor when Pixelated is active, then applies Monochrome as a
   * pixel-remap post-process. */
  private present(): void {
    const { pixelLevel, monochromeLevel } = this.effects;

    if (pixelLevel === 0) {
      this.ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      this.ctx.drawImage(this.sourceCanvas, 0, 0);
    } else if (this.pixelCanvas) {
      this.ctx.imageSmoothingEnabled = false;
      this.ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      this.ctx.drawImage(this.pixelCanvas, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      this.ctx.imageSmoothingEnabled = true;
    }

    if (monochromeLevel > 0) {
      const imageData = this.ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        const gray = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
        const value = monochromeLevel >= 2 ? (gray >= 128 ? 255 : 0) : gray;
        data[i] = value;
        data[i + 1] = value;
        data[i + 2] = value;
      }
      this.ctx.putImageData(imageData, 0, 0);
    }
  }
}
